import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap,
  applyNodeChanges, applyEdgeChanges, addEdge,
  Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import toast from 'react-hot-toast';
import api from '../api';

/* ─── Node catalog ─────────────────────────────────────────────────────────── */

const NODE_META = {
  trigger:       { icon: '⚡', label: 'Trigger',       color: '#f59e0b', desc: 'Starts from a keyword or template button' },
  send_text:     { icon: '💬', label: 'Send Message',  color: '#6366f1', desc: 'Send a text message' },
  capture:       { icon: '📝', label: 'Ask & Capture', color: '#8b5cf6', desc: 'Ask a question, save the reply' },
  buttons:       { icon: '🔘', label: 'Buttons',       color: '#0ea5e9', desc: 'Quick replies — branch per button' },
  condition:     { icon: '🔀', label: 'Condition',     color: '#f97316', desc: 'Branch on the last reply' },
  tag:           { icon: '🏷️', label: 'Tag Contact',   color: '#22c55e', desc: 'Add or remove a tag' },
  subscription:  { icon: '🔕', label: 'Opt In/Out',    color: '#ef4444', desc: 'Subscribe or unsubscribe the contact' },
  send_template: { icon: '📄', label: 'Send Template', color: '#64748b', desc: 'Send an approved template' },
  send_flow:     { icon: '🧾', label: 'Send Flow',     color: '#ec4899', desc: 'Send a WhatsApp Flow form' },
};

const DEFAULT_DATA = {
  trigger:       { keywords: 'JOIN', match: 'exact' },
  send_text:     { text: 'Hello {{name}}! 👋' },
  capture:       { question: 'What is your email address?', save_field: 'email' },
  buttons:       { text: 'What would you like to do?', buttons: ['Subscribe', 'Learn more'] },
  condition:     { value: 'YES', match: 'contains' },
  tag:           { tag: '', action: 'add' },
  subscription:  { action: 'opt_out' },
  send_template: { template_name: '', language: 'en_US', params: '' },
  send_flow:     { flow_id: '', flow_cta: 'Open Form', flow_header: '', flow_body: 'Please fill out the form below.' },
};

/* ─── Custom node renderers ────────────────────────────────────────────────── */

function NodeShell({ type, selected, children, sourceHandles }) {
  const meta = NODE_META[type];
  return (
    <div style={{
      minWidth: 200, maxWidth: 260, borderRadius: 10, overflow: 'visible',
      background: 'var(--surface, #fff)',
      border: `2px solid ${selected ? meta.color : 'var(--border, #e2e8f0)'}`,
      boxShadow: selected ? `0 0 0 3px ${meta.color}33` : '0 2px 8px rgba(0,0,0,0.08)',
      fontSize: 12, color: 'var(--text, #0f172a)',
    }}>
      {type !== 'trigger' && <Handle type="target" position={Position.Left} style={{ background: meta.color, width: 10, height: 10 }} />}
      <div style={{ padding: '7px 12px', fontWeight: 700, borderBottom: '1px solid var(--border, #e2e8f0)', background: `${meta.color}18`, borderRadius: '8px 8px 0 0' }}>
        {meta.icon} {meta.label}
      </div>
      <div style={{ padding: '8px 12px', lineHeight: 1.5 }}>{children}</div>
      {sourceHandles
        ? sourceHandles.map((h, i) => (
          <div key={h.id} style={{ position: 'relative', padding: '4px 12px', borderTop: '1px solid var(--border, #e2e8f0)', textAlign: 'right', fontSize: 11, color: 'var(--text-muted, #64748b)' }}>
            {h.label}
            <Handle type="source" id={h.id} position={Position.Right}
              style={{ background: meta.color, width: 10, height: 10, top: '50%' }} />
          </div>
        ))
        : <Handle type="source" position={Position.Right} style={{ background: meta.color, width: 10, height: 10 }} />
      }
    </div>
  );
}

const truncate = (s, n = 60) => (s || '').length > n ? s.slice(0, n) + '…' : (s || '');
const templateButtonLabels = template => (template?.components || [])
  .find(c => c.type?.toUpperCase() === 'BUTTONS')?.buttons
  ?.map(button => button.type?.toUpperCase() === 'QUICK_REPLY' ? button.text : null) || [];

function TriggerNode({ data, selected }) {
  if (data.mode === 'template_button') return (
    <NodeShell type="trigger" selected={selected}
      sourceHandles={(data.button_labels || []).map((label, index) => label && ({ id: `btn_${index}`, label: `🔘 ${label}` })).filter(Boolean)}>
      Template: <strong>{data.template_name || '—'}</strong>
      <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>{data.language || '—'} quick replies</div>
    </NodeShell>
  );
  return (
    <NodeShell type="trigger" selected={selected}>
      Keywords: <strong>{data.keywords || '—'}</strong>
      <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>{data.match === 'contains' ? 'message contains' : 'exact match'}</div>
    </NodeShell>
  );
}
function SendTextNode({ data, selected }) {
  return <NodeShell type="send_text" selected={selected}>{truncate(data.text) || <em>No message set</em>}</NodeShell>;
}
function CaptureNode({ data, selected }) {
  return (
    <NodeShell type="capture" selected={selected}>
      {truncate(data.question) || <em>No question set</em>}
      <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>saves to → {data.save_field || 'notes'}</div>
    </NodeShell>
  );
}
function ButtonsNode({ data, selected }) {
  const btns = (data.buttons || []).filter(Boolean);
  return (
    <NodeShell type="buttons" selected={selected}
      sourceHandles={btns.map((b, i) => ({ id: `btn_${i}`, label: `🔘 ${b}` }))}>
      {truncate(data.text, 50) || <em>No prompt set</em>}
    </NodeShell>
  );
}
function ConditionNode({ data, selected }) {
  return (
    <NodeShell type="condition" selected={selected}
      sourceHandles={[{ id: 'yes', label: '✔ matches' }, { id: 'no', label: '✘ otherwise' }]}>
      Reply {data.match === 'contains' ? 'contains' : 'equals'}: <strong>{data.value || '—'}</strong>
    </NodeShell>
  );
}
function TagNode({ data, selected }) {
  return (
    <NodeShell type="tag" selected={selected}>
      {data.action === 'remove' ? 'Remove tag' : 'Add tag'}: <strong>{data.tag_name || data.tag || '—'}</strong>
    </NodeShell>
  );
}
function SubscriptionNode({ data, selected }) {
  return (
    <NodeShell type="subscription" selected={selected}>
      {data.action === 'opt_in'
        ? <>✅ <strong>Opt the contact in</strong></>
        : <>🚫 <strong>Opt the contact out</strong></>}
      <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>
        {data.action === 'opt_in' ? 'contact will receive broadcasts' : 'excluded from all broadcasts'}
      </div>
    </NodeShell>
  );
}
function SendTemplateNode({ data, selected }) {
  return (
    <NodeShell type="send_template" selected={selected}
      sourceHandles={data.wait_for_reply
        ? (data.button_labels || []).map((label, index) => label && ({ id: `btn_${index}`, label: `🔘 ${label}` })).filter(Boolean)
        : undefined}>
      Template: <strong>{data.template_name || '—'}</strong>
      <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>{data.language || 'en_US'}</div>
      {data.wait_for_reply && <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>Waits for a quick reply</div>}
    </NodeShell>
  );
}
function SendFlowNode({ data, selected }) {
  return (
    <NodeShell type="send_flow" selected={selected}>
      Flow ID: <strong>{truncate(data.flow_id, 24) || '—'}</strong>
      <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 11 }}>CTA: {data.flow_cta || 'Open Form'}</div>
    </NodeShell>
  );
}

const nodeTypes = {
  trigger: TriggerNode, send_text: SendTextNode, capture: CaptureNode,
  buttons: ButtonsNode, condition: ConditionNode, tag: TagNode,
  subscription: SubscriptionNode, send_template: SendTemplateNode, send_flow: SendFlowNode,
};

/* ─── Editor page ──────────────────────────────────────────────────────────── */

export default function AutomationEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [active, setActive] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadTemplates = useCallback(() => {
    setTemplatesLoading(true);
    api.get('/automations/templates')
      .then(r => { setTemplates(r.data); setTemplatesError(null); })
      .catch(err => { setTemplates([]); setTemplatesError(err.response?.data?.message || 'Failed to load templates'); })
      .finally(() => setTemplatesLoading(false));
  }, []);

  useEffect(() => {
    api.get(`/automations/${id}`).then(r => {
      setName(r.data.name);
      setActive(r.data.active);
      setNodes(r.data.nodes || []);
      setEdges(r.data.edges || []);
      setLoaded(true);
    }).catch(() => { toast.error('Automation not found'); navigate('/automations'); });
    api.get('/tags').then(r => setTags(r.data)).catch(() => {});
    loadTemplates();
  }, [id, navigate, loadTemplates]);

  const onNodesChange = useCallback(changes => {
    setNodes(ns => applyNodeChanges(changes, ns));
    if (changes.some(c => c.type !== 'select' && c.type !== 'dimensions')) setDirty(true);
  }, []);
  const onEdgesChange = useCallback(changes => {
    setEdges(es => applyEdgeChanges(changes, es));
    if (changes.some(c => c.type !== 'select')) setDirty(true);
  }, []);
  const onConnect = useCallback(conn => {
    setEdges(es => {
      // One outgoing edge per source handle — replace any existing one
      const filtered = es.filter(e => !(e.source === conn.source && (e.sourceHandle || null) === (conn.sourceHandle || null)));
      return addEdge({ ...conn, animated: true }, filtered);
    });
    setDirty(true);
  }, []);

  const selected = useMemo(() => nodes.find(n => n.id === selectedId) || null, [nodes, selectedId]);

  function addNode(type) {
    const nodeId = `${type}_${Date.now()}`;
    setNodes(ns => [...ns, {
      id: nodeId, type,
      position: { x: 320 + Math.random() * 120, y: 120 + Math.random() * 200 },
      data: { ...DEFAULT_DATA[type] },
    }]);
    setSelectedId(nodeId);
    setDirty(true);
  }

  function updateSelectedData(patch) {
    setNodes(ns => ns.map(n => n.id === selectedId ? { ...n, data: { ...n.data, ...patch } } : n));
    if ((selected?.type === 'trigger' && (patch.mode !== undefined || patch.template_name !== undefined))
      || (selected?.type === 'send_template' && (patch.template_name !== undefined || patch.wait_for_reply !== undefined))) {
      setEdges(es => es.filter(edge => edge.source !== selectedId));
    }
    setDirty(true);
  }

  function deleteSelected() {
    if (!selected || selected.type === 'trigger') return;
    setNodes(ns => ns.filter(n => n.id !== selectedId));
    setEdges(es => es.filter(e => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
    setDirty(true);
  }

  async function save(activeOverride) {
    setSaving(true);
    try {
      const r = await api.put(`/automations/${id}`, {
        name, nodes, edges,
        ...(activeOverride !== undefined ? { active: activeOverride } : {}),
      });
      if (activeOverride !== undefined) setActive(r.data.active);
      setDirty(false);
      toast.success(activeOverride === true ? 'Saved & activated! 🚀' : 'Saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  }

  if (!loaded) return <div style={{ color: 'var(--text-muted)' }}>Loading canvas…</div>;

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)', margin: -8 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/automations')}>← Back</button>
        <input className="input" value={name} onChange={e => { setName(e.target.value); setDirty(true); }}
          style={{ fontWeight: 700, fontSize: 15, width: 260 }} />
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
          background: active ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
          color: active ? 'var(--success)' : 'var(--text-muted)',
        }}>
          {active ? '● Live' : '○ Paused'}
        </span>
        {dirty && <span style={{ fontSize: 11, color: 'var(--warning)' }}>● Unsaved changes</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => save()} disabled={saving}>
            {saving ? 'Saving…' : '💾 Save'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => save(!active)} disabled={saving}>
            {active ? '⏸ Pause' : '🚀 Save & Activate'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Node palette */}
        <div style={{ width: 190, borderRight: '1px solid var(--border)', padding: 10, overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Add a step
          </div>
          {Object.entries(NODE_META).filter(([t]) => t !== 'trigger').map(([type, meta]) => (
            <button key={type} onClick={() => addNode(type)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: 6,
                padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                background: 'var(--surface2)', border: '1px solid var(--border)',
                color: 'var(--text)', fontSize: 12,
              }}>
              <span style={{ marginRight: 6 }}>{meta.icon}</span><strong>{meta.label}</strong>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{meta.desc}</div>
            </button>
          ))}
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.6 }}>
            💡 Drag from a node's right handle to the next node's left handle to connect steps.
            Use <code>{'{{name}}'}</code>, <code>{'{{email}}'}</code>, <code>{'{{phone}}'}</code> in messages.
          </div>
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            colorMode={isDark ? 'dark' : 'light'}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={18} />
            <Controls />
            <MiniMap pannable zoomable style={{ width: 140, height: 90 }} />
          </ReactFlow>
        </div>

        {/* Config panel */}
        {selected && (
          <div style={{ width: 280, borderLeft: '1px solid var(--border)', padding: 14, overflowY: 'auto', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>
                {NODE_META[selected.type].icon} {NODE_META[selected.type].label}
              </div>
              {selected.type !== 'trigger' && (
                <button className="btn btn-danger btn-sm" onClick={deleteSelected}>🗑</button>
              )}
            </div>
            <NodeConfig node={selected} tags={tags} templates={templates}
              templatesLoading={templatesLoading} templatesError={templatesError}
              onRefreshTemplates={loadTemplates} onChange={updateSelectedData} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Per-node config forms ────────────────────────────────────────────────── */

function Field({ label, children }) {
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label className="label" style={{ fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}

function NodeConfig({ node, tags, templates, templatesLoading, templatesError, onRefreshTemplates, onChange }) {
  const d = node.data || {};

  switch (node.type) {
    case 'trigger':
      return (
        <>
          <Field label="Start when">
            <select className="input" value={d.mode || 'keyword'} onChange={e => onChange({ mode: e.target.value })}>
              <option value="keyword">Customer sends a keyword</option>
              <option value="template_button">Customer taps a Meta template button</option>
            </select>
          </Field>
          {d.mode === 'template_button' ? <>
            <Field label="Approved Meta template">
              <select className="input" value={JSON.stringify([d.template_name || '', d.language || ''])}
                disabled={templatesLoading || !!templatesError}
                onChange={e => {
                  const template = templates.find(t => JSON.stringify([t.name, t.language]) === e.target.value);
                  onChange(template ? { template_name: template.name, language: template.language,
                    button_labels: templateButtonLabels(template) }
                    : { template_name: '', language: '', button_labels: [] });
                }}>
                <option value={JSON.stringify(['', ''])}>Select a template</option>
                {templates.filter(t => templateButtonLabels(t).some(Boolean)).map(t => (
                  <option key={`${t.name}:${t.language}`} value={JSON.stringify([t.name, t.language])}>
                    {t.name} ({t.language})
                  </option>
                ))}
              </select>
            </Field>
            {templatesError && <p style={{ fontSize: 11, color: 'var(--danger)' }}>{templatesError}</p>}
            <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Connect each quick reply handle to the response for that button. The template may be sent by a broadcast or another automation.
            </p>
          </> : <>
            <Field label="Trigger keywords (comma-separated)">
              <input className="input" value={d.keywords || ''} placeholder="JOIN, SUBSCRIBE"
                onChange={e => onChange({ keywords: e.target.value })} />
            </Field>
            <Field label="Match type">
              <select className="input" value={d.match || 'exact'} onChange={e => onChange({ match: e.target.value })}>
                <option value="exact">Message equals keyword</option>
                <option value="contains">Message contains keyword</option>
              </select>
            </Field>
          </>}
        </>
      );

    case 'send_text':
      return (
        <Field label="Message text">
          <textarea className="textarea" rows={5} value={d.text || ''}
            placeholder="Hello {{name}}! 👋"
            onChange={e => onChange({ text: e.target.value })} />
        </Field>
      );

    case 'capture':
      return (
        <>
          <Field label="Question to ask">
            <textarea className="textarea" rows={3} value={d.question || ''}
              onChange={e => onChange({ question: e.target.value })} />
          </Field>
          <Field label="Save the reply to">
            <select className="input" value={d.save_field || 'notes'} onChange={e => onChange({ save_field: e.target.value })}>
              <option value="name">Contact name</option>
              <option value="email">Contact email</option>
              <option value="notes">Contact notes</option>
            </select>
          </Field>
          <Field label="Validate reply as">
            <select className="input" value={d.validate || 'none'} onChange={e => onChange({ validate: e.target.value })}>
              <option value="none">No validation</option>
              <option value="email">Email address</option>
              <option value="phone">Phone number</option>
              <option value="number">Number</option>
            </select>
          </Field>
          {d.validate && d.validate !== 'none' && (
            <>
              <Field label="Message when reply is invalid">
                <textarea className="textarea" rows={2} value={d.retry_message || ''}
                  placeholder="Hmm, that doesn't look right — please try again."
                  onChange={e => onChange({ retry_message: e.target.value })} />
              </Field>
              <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Invalid replies are re-asked up to 3 times, then the flow continues without saving.
              </p>
            </>
          )}
        </>
      );

    case 'buttons': {
      const btns = d.buttons || [];
      return (
        <>
          <Field label="Prompt text">
            <textarea className="textarea" rows={3} value={d.text || ''}
              onChange={e => onChange({ text: e.target.value })} />
          </Field>
          <Field label="Buttons (max 3, 20 chars each)">
            {[0, 1, 2].map(i => (
              <input key={i} className="input" style={{ marginBottom: 6 }} maxLength={20}
                placeholder={i === 0 ? 'Button 1 (required)' : `Button ${i + 1} (optional)`}
                value={btns[i] || ''}
                onChange={e => {
                  const next = [...btns];
                  next[i] = e.target.value;
                  onChange({ buttons: next });
                }} />
            ))}
          </Field>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Each button gets its own output handle — connect each to a different next step.
          </p>
        </>
      );
    }

    case 'condition':
      return (
        <>
          <Field label="Values to match (comma-separated)">
            <input className="input" value={d.value || ''} placeholder="YES, YEAH, OK"
              onChange={e => onChange({ value: e.target.value })} />
          </Field>
          <Field label="Match type">
            <select className="input" value={d.match || 'contains'} onChange={e => onChange({ match: e.target.value })}>
              <option value="contains">Reply contains value</option>
              <option value="equals">Reply equals value</option>
            </select>
          </Field>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Checks the contact's most recent reply. "✔ matches" and "✘ otherwise" each have their own output.
          </p>
        </>
      );

    case 'tag':
      return (
        <>
          <Field label="Action">
            <select className="input" value={d.action || 'add'} onChange={e => onChange({ action: e.target.value })}>
              <option value="add">Add tag</option>
              <option value="remove">Remove tag</option>
            </select>
          </Field>
          <Field label="Tag">
            <select className="input" value={d.tag || ''}
              onChange={e => {
                const t = tags.find(t => t._id === e.target.value);
                onChange({ tag: e.target.value, tag_name: t?.name || '' });
              }}>
              <option value="">— select a tag —</option>
              {tags.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          </Field>
          {tags.length === 0 && (
            <p style={{ fontSize: 11, color: 'var(--warning)' }}>No tags yet — create one in Settings first.</p>
          )}
        </>
      );

    case 'subscription':
      return (
        <>
          <Field label="Action">
            <select className="input" value={d.action || 'opt_out'} onChange={e => onChange({ action: e.target.value })}>
              <option value="opt_out">Opt out — stop broadcasts</option>
              <option value="opt_in">Opt in — resume broadcasts</option>
            </select>
          </Field>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Opted-out contacts are automatically excluded from every broadcast.
            Replying STOP, UNSUBSCRIBE or OPTOUT always opts a contact out, even mid-automation.
          </p>
        </>
      );

    case 'send_template': {
      const selectedTemplate = templates.find(t => t.name === d.template_name && t.language === (d.language || 'en_US'));
      return (
        <>
          <Field label="Approved Meta template">
            <select className="input" value={selectedTemplate ? JSON.stringify([selectedTemplate.name, selectedTemplate.language]) : ''}
              disabled={templatesLoading || !!templatesError || templates.length === 0}
              onChange={e => {
                const template = templates.find(t => JSON.stringify([t.name, t.language]) === e.target.value);
                onChange(template
                  ? { template_name: template.name, language: template.language, category: template.category,
                    button_labels: templateButtonLabels(template), wait_for_reply: false, params: '' }
                  : { template_name: '', language: 'en_US', category: undefined,
                    button_labels: [], wait_for_reply: false, params: '' });
              }}>
              <option value="">Select a template</option>
              {templates.map(t => (
                <option key={`${t.name}:${t.language}`} value={JSON.stringify([t.name, t.language])}>
                  {t.name} ({t.language})
                </option>
              ))}
            </select>
          </Field>
          {templatesLoading && <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>Loading approved templates from Meta…</p>}
          {templatesError && <p style={{ fontSize: 11, color: 'var(--danger)' }}>{templatesError}</p>}
          {!templatesLoading && !templatesError && templates.length === 0 &&
            <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>No approved templates found for this WABA.</p>}
          {d.template_name && !templatesLoading && !templatesError && !selectedTemplate &&
            <p style={{ fontSize: 11, color: 'var(--warning)' }}>
              {d.template_name} ({d.language || 'en_US'}) is no longer approved on this WABA. Select an approved template to replace it.
            </p>}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRefreshTemplates} disabled={templatesLoading}>
            ↻ Refresh templates
          </button>
          <Field label="Body parameters (comma-separated)">
            <input className="input" value={d.params || ''} placeholder="{{name}}, ACME"
              onChange={e => onChange({ params: e.target.value })} />
          </Field>
          {(d.button_labels || []).some(Boolean) && <>
            <Field label="Wait for quick reply">
              <input type="checkbox" checked={!!d.wait_for_reply}
                onChange={e => onChange({ wait_for_reply: e.target.checked })} />
            </Field>
            {d.wait_for_reply && <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Connect each button output to the next step. This automation resumes when the customer taps a button on this template.
            </p>}
          </>}
        </>
      );
    }

    case 'send_flow':
      return (
        <>
          <Field label="Flow ID">
            <input className="input" value={d.flow_id || ''}
              onChange={e => onChange({ flow_id: e.target.value })} />
          </Field>
          <Field label="Button label (CTA)">
            <input className="input" value={d.flow_cta || ''} maxLength={20}
              onChange={e => onChange({ flow_cta: e.target.value })} />
          </Field>
          <Field label="Header (optional)">
            <input className="input" value={d.flow_header || ''}
              onChange={e => onChange({ flow_header: e.target.value })} />
          </Field>
          <Field label="Body text">
            <textarea className="textarea" rows={3} value={d.flow_body || ''}
              onChange={e => onChange({ flow_body: e.target.value })} />
          </Field>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Requires Meta business verification — the rest of the canvas works without it.
          </p>
        </>
      );

    default:
      return null;
  }
}
